/**
 * SEO Metadata and Google SERP Pixel Width Calculator
 *
 * Requirements:
 * - Meta title: MAX 512 px (Google desktop title font: Arial 20px regular)
 * - Meta description: MAX 970 px (Google desktop snippet font: Arial 14px regular)
 */

// Precise proportional Arial widths (units per 1000em)
const ARIAL_WIDTHS = {
  ' ': 278, '!': 278, '"': 355, '#': 556, '$': 556, '%': 889, '&': 667, '\'': 191,
  '(': 333, ')': 333, '*': 389, '+': 584, ',': 278, '-': 333, '.': 278, '/': 278,
  '0': 556, '1': 556, '2': 556, '3': 556, '4': 556, '5': 556, '6': 556, '7': 556,
  '8': 556, '9': 556, ':': 278, ';': 278, '<': 584, '=': 584, '>': 584, '?': 556,
  '@': 1015, 'A': 667, 'B': 667, 'C': 722, 'D': 722, 'E': 667, 'F': 611, 'G': 778,
  'H': 722, 'I': 278, 'J': 500, 'K': 667, 'L': 556, 'M': 833, 'N': 722, 'O': 778,
  'P': 667, 'Q': 778, 'R': 722, 'S': 667, 'T': 611, 'U': 722, 'V': 667, 'W': 944,
  'X': 667, 'Y': 667, 'Z': 611, '[': 278, '\\': 278, ']': 278, '^': 469, '_': 556,
  '`': 333, 'a': 556, 'b': 556, 'c': 500, 'd': 556, 'e': 556, 'f': 278, 'g': 556,
  'h': 556, 'i': 222, 'j': 222, 'k': 500, 'l': 222, 'm': 833, 'n': 556, 'o': 556,
  'p': 556, 'q': 556, 'r': 333, 's': 500, 't': 278, 'u': 556, 'v': 500, 'w': 722,
  'x': 500, 'y': 500, 'z': 500, '{': 334, '|': 260, '}': 334, '~': 584,
  // Danish special characters
  'æ': 889, 'ø': 611, 'å': 556, 'Æ': 1000, 'Ø': 778, 'Å': 667,
  '–': 556, '—': 1000, '•': 350, 'é': 556, 'É': 667, 'è': 556, 'ü': 556
};

/**
 * Calculate exact pixel width for Arial 20px (Google Title)
 */
export function calculateTitlePixelWidth(text) {
  if (!text) return 0;
  if (typeof document !== "undefined") {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.font = "20px Arial";
        const width = ctx.measureText(text).width;
        if (width > 0) return Math.round(width * 10) / 10;
      }
    } catch {
      // fallback to formula
    }
  }

  let units = 0;
  for (const char of text) {
    units += ARIAL_WIDTHS[char] || 550;
  }
  return Math.round((units / 1000) * 20 * 10) / 10;
}

/**
 * Calculate exact pixel width for Arial 14px (Google Snippet Description)
 */
export function calculateDescriptionPixelWidth(text) {
  if (!text) return 0;
  if (typeof document !== "undefined") {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.font = "14px Arial";
        const width = ctx.measureText(text).width;
        if (width > 0) return Math.round(width * 10) / 10;
      }
    } catch {
      // fallback to formula
    }
  }

  let units = 0;
  for (const char of text) {
    units += ARIAL_WIDTHS[char] || 550;
  }
  return Math.round((units / 1000) * 14 * 10) / 10;
}

export const MAX_TITLE_PIXELS = 512;
export const MAX_DESCRIPTION_PIXELS = 970;

// Default brand and share images
export const DEFAULT_OG_IMAGE =
  "https://assets.auxodev.dk/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/toemrer-jg-mesterbyg-erslev-mors.jpg";

/**
 * Curated metadata dictionary for every route in the website
 * Strictly audited to guarantee Title <= 512 px and Description <= 970 px
 */
export const PAGE_SEO_CONFIG = {
  home: {
    title: "Tømrer i Erslev på Mors & omegn | JG Mesterbyg",
    // ~447 px (< 512 px)
    description:
      "Søger du en tømrer i Erslev på Mors og omegn? JG Mesterbyg udfører renovering, udskiftning af vinduer og døre samt nyt tag. Få et uforpligtende tilbud i dag.",
    // ~915 px (< 970 px)
    canonical: "/",
    image: DEFAULT_OG_IMAGE,
  },
  "udskiftning-af-vinduer-og-doere": {
    title: "Vinduer & døre i Erslev, Skive & Mors | JG Mesterbyg",
    // ~488 px (< 512 px)
    description:
      "Udskiftning af vinduer og døre i Skive, Erslev på Mors, Thisted og Nykøbing Mors. Vi rådgiver om træ, træ/alu og plast samt korrekt tætning. Få et tilbud.",
    // ~934 px (< 970 px)
    canonical: "/ydelser/udskiftning-af-vinduer-og-doere",
    image:
      "https://assets.auxodev.dk/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/montering-hvide-vinduer-og-terrassedoer.jpg",
  },
  renovering: {
    title: "Renovering af hus på Mors & i Skive | JG Mesterbyg",
    // ~475 px (< 512 px)
    description:
      "Professionel renovering af hus i Skive, Erslev på Mors, Thisted og Nykøbing Mors. Vi klarer alt fra køkken og lofter til totalrenovering. Få et tilbud.",
    // ~923 px (< 970 px)
    canonical: "/ydelser/renovering",
    image:
      "https://assets.auxodev.dk/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/akustikloft-traebeton-renoveret-rum.jpg",
  },
  "nyt-tag": {
    title: "Nyt tag på Mors, i Thisted & Skive | JG Mesterbyg",
    // ~470 px (< 512 px)
    description:
      "Nyt tag eller reparation af tag på Mors, i Thisted, Skive og omegn. JG Mesterbyg rådgiver om tegl, skifer, stål, undertag og pris. Få et uforpligtende tilbud.",
    // ~930 px (< 970 px)
    canonical: "/ydelser/nyt-tag",
    image:
      "https://assets.auxodev.dk/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/tagrenovering-roedstenshus-tagsten-tagvinduer.jpg",
  },
  koekkenrenovering: {
    title: "Køkkenrenovering på Mors & i Skive | JG Mesterbyg",
    // ~478 px (< 512 px)
    description:
      "Få monteret dit nye køkken med millimeterpræcision. JG Mesterbyg hjælper med renovering og montering af alle køkkenmærker på Mors og i Skive. Få et tilbud.",
    // ~937 px (< 970 px)
    canonical: "/ydelser/koekkenrenovering",
    image:
      "https://assets.auxodev.dk/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/koekkenrenovering-beige-elementer-traegulv.jpg",
  },
  "opsaetning-af-solceller": {
    title: "Opsætning af solceller på Mors | JG Mesterbyg",
    // ~434 px (< 512 px)
    description:
      "Professionel opsætning af solceller på alle typer tag. Vi sikrer en tæt og holdbar montering på Mors, i Thisted, Skive og Nordjylland. Få et tilbud her.",
    // ~924 px (< 970 px)
    canonical: "/ydelser/opsaetning-af-solceller",
    image:
      "https://assets.auxodev.dk/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/montering-nyt-tag-laegter-landejendom.jpg",
  },
  quote: {
    title: "Få et uforpligtende tilbud | JG Mesterbyg",
    // ~403 px (< 512 px)
    description:
      "Få et uforpligtende tilbud på tømrerarbejde, renovering, tag, vinduer eller køkken fra JG Mesterbyg. Beskriv dit projekt og få overblik over prisen.",
    // ~905 px (< 970 px)
    canonical: "/fa-tilbud",
    image: DEFAULT_OG_IMAGE,
  },
  contact: {
    title: "Kontakt JG Mesterbyg | Tømrer i Erslev på Mors",
    // ~452 px (< 512 px)
    description:
      "Kontakt JG Mesterbyg i Erslev på Mors. Ring på 27 29 41 87 eller send en besked for rådgivning og tilbud på dit byggeprojekt i lokalområdet.",
    // ~876 px (< 970 px)
    canonical: "/kontakt",
    image: DEFAULT_OG_IMAGE,
  },
  gallery: {
    title: "Galleri over udført arbejde | JG Mesterbyg",
    // ~414 px (< 512 px)
    description:
      "Se billeder af vores udførte arbejde med renovering, tagarbejde, vinduer, døre, køkken og solceller på Mors og i Nordjylland. Se eksemplerne her.",
    // ~895 px (< 970 px)
    canonical: "/galleri",
    image:
      "https://assets.auxodev.dk/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/montering-hvide-vinduer-og-terrassedoer.jpg",
  },
  notFound: {
    title: "Siden blev ikke fundet (404) | JG Mesterbyg",
    // ~416 px (< 512 px)
    description:
      "Siden du leder efter findes ikke. Gå til forsiden for at se vores ydelser inden for tømrerarbejde, renovering og tagarbejde hos JG Mesterbyg.",
    // ~891 px (< 970 px)
    noIndex: true,
  },
  forbidden: {
    title: "Adgang nægtet (403) | JG Mesterbyg",
    // ~343 px (< 512 px)
    description:
      "Du har ikke adgang til denne side. Kontakt JG Mesterbyg hvis du mener dette er en fejl, eller gå tilbage til vores forside.",
    // ~750 px (< 970 px)
    noIndex: true,
  },
};

/**
 * Apply metadata to document head imperatively
 */
export function applyPageMeta(meta) {
  if (typeof document === "undefined") return;

  const title = meta.title || "JG Mesterbyg - Professionelt tømrerarbejde";
  const description =
    meta.description ||
    "JG Mesterbyg tilbyder tømrerarbejde, renovering, vinduer, tag og solceller på Mors og i Nordjylland.";
  const canonical = meta.canonical
    ? `https://jgmesterbyg.dk${meta.canonical}`
    : window.location.href;
  const image = meta.image || DEFAULT_OG_IMAGE;
  const noIndex = Boolean(meta.noIndex);

  // Set Title
  document.title = title;

  // Helper to get or create a tag
  const setMetaTag = (selector, createFn) => {
    let el = document.querySelector(selector);
    if (!el) {
      el = createFn();
      document.head.appendChild(el);
    }
    return el;
  };

  // Description
  const descEl = setMetaTag('meta[name="description"]', () => {
    const el = document.createElement("meta");
    el.setAttribute("name", "description");
    return el;
  });
  descEl.setAttribute("content", description);

  // Canonical
  let canonicalEl = document.querySelector('link[rel="canonical"]');
  if (!canonicalEl) {
    canonicalEl = document.createElement("link");
    canonicalEl.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalEl);
  }
  canonicalEl.setAttribute("href", canonical);

  // Open Graph
  const setOg = (property, content) => {
    const el = setMetaTag(`meta[property="${property}"]`, () => {
      const newEl = document.createElement("meta");
      newEl.setAttribute("property", property);
      return newEl;
    });
    el.setAttribute("content", content);
  };

  setOg("og:title", title);
  setOg("og:description", description);
  setOg("og:type", "website");
  setOg("og:url", canonical);
  setOg("og:image", image);
  setOg("og:locale", "da_DK");
  setOg("og:site_name", "JG Mesterbyg");

  // Twitter Cards
  const setTwitter = (name, content) => {
    const el = setMetaTag(`meta[name="${name}"]`, () => {
      const newEl = document.createElement("meta");
      newEl.setAttribute("name", name);
      return newEl;
    });
    el.setAttribute("content", content);
  };

  setTwitter("twitter:card", "summary_large_image");
  setTwitter("twitter:title", title);
  setTwitter("twitter:description", description);
  setTwitter("twitter:image", image);

  // Robots
  const robotsEl = setMetaTag('meta[name="robots"]', () => {
    const el = document.createElement("meta");
    el.setAttribute("name", "robots");
    return el;
  });
  robotsEl.setAttribute(
    "content",
    noIndex ? "noindex, nofollow" : "index, follow"
  );
}
